import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Users, ChevronRight, Play } from "lucide-react";
import api from "../lib/api";

/**
 * LiveNowHero — home-page "LIVE NOW" surface. Queries GET /api/live
 * and shows the top 3 currently-live streams as tap-to-join hero cards.
 * Silently renders nothing when no one's live (avoids the "empty
 * awkward row" that makes a platform feel dead). Auto-refreshes every
 * 45s so viewers see new streams without a manual reload.
 */
export default function LiveNowHero() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get("/live")
        .then(({ data }) => {
          if (cancelled) return;
          const list = Array.isArray(data) ? data.slice(0, 3) : [];
          setItems(list);
          setLoaded(true);
        })
        .catch(() => { if (!cancelled) setLoaded(true); });
    };
    load();
    const id = setInterval(load, 45_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!loaded || items.length === 0) return null;

  return (
    <section data-testid="live-now-hero" className="mb-8 sm:mb-12">
      <div className="flex items-center justify-between mb-3 sm:mb-4 px-1">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 bg-tv-live/90 text-white text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
          </span>
          <h2 className="font-display font-black text-white text-lg sm:text-xl tracking-tight">Live right now</h2>
        </div>
        <Link
          to="/live"
          data-testid="live-now-see-all"
          className="text-tv-accent hover:text-yellow-400 text-xs font-black uppercase tracking-wider inline-flex items-center gap-0.5"
        >See all <ChevronRight className="w-3 h-3" /></Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        {items.map((s) => (
          <Link
            key={s.id}
            to={`/live/${s.id}`}
            data-testid={`live-now-card-${s.id}`}
            className="group relative aspect-video rounded-xl overflow-hidden bg-black border border-white/10 hover:border-tv-accent/60 transition"
          >
            {(s.thumbnail || s.cover_image) ? (
              <img
                src={s.thumbnail || s.cover_image}
                alt={s.title || "Live stream"}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-tv-live/20 to-black">
                <Radio className="w-10 h-10 text-tv-live/70" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
            <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-tv-live text-white text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded">
              <span className="w-1 h-1 rounded-full bg-white animate-pulse" /> LIVE
            </div>
            <div className="absolute top-2 right-2 inline-flex items-center gap-1 bg-black/70 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
              <Users className="w-2.5 h-2.5" /> {(s.viewer_count || 0).toLocaleString()}
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-3">
              <div className="text-white font-black text-sm sm:text-base leading-tight line-clamp-2 mb-0.5">
                {s.title || "Untitled stream"}
              </div>
              <div className="text-white/70 text-[11px] truncate flex items-center gap-1">
                <Play className="w-3 h-3 text-tv-accent" />
                {s.creator_name || s.host_name || "Anonymous"}
                {s.category && <span className="text-white/40">· {s.category}</span>}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
